import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Text, HStack, Icon, VStack, Collapse, Flex } from "@chakra-ui/react";
import { ArtifactProto, ExecutionStepProto } from "build-ts/chat_session";
import { ExecutionStatusType } from "build-ts/common";
import { formatDurationFromDeltaMs, minMaxExecutionStepTimestampsMs } from "../../../utils/helperFuncUtils";
import { groupExecutionStepsByToolRun } from "../../../utils/executionStepRun";
import { AppIcons } from "constants/icons";
import { ArtifactOpenOpts, ReasoningGroup, TimelineEntry, TimelineItem, TimelineKind } from "../../interfaces";
import ThoughtBlock from "./ThoughtBlock";
import ToolRunRow from "./ToolRunRow";
import AgentThinkingLogo from "./AgentThinkingLogo";

export interface ExecutionStepsCardProps {
  onArtifactClick?: (artifact: ArtifactProto, opts?: ArtifactOpenOpts) => void;
  executionSteps?: ExecutionStepProto[];
  /** Reasoning thought blocks streamed from the LLM — each group maps to one collapsible "Thought" block. */
  streamingReasoningGroups?: ReasoningGroup[];
  /**
   * Unified timeline order for the streaming case — appended in stream arrival order.
   * When present, this drives the timeline directly (no sorting needed).
   * Absent for non-streaming (historical) messages; the timeline is derived from executionSteps.
   */
  timelineOrder?: TimelineEntry[];
  /** Timestamp (ms) of the REASONING proto message — used to compute the first reasoning block's duration on reload. */
  prevMessageEndTimestampMs?: number;
  /** While the assistant message is still streaming, show Thinking vs Working instead of the final summary. */
  isStreaming?: boolean;
  /**
   * True when assistant has started streaming reply text; triggers "Worked for" collapse
   * and hides the live "Working/Thinking" status row.
   */
  hasStreamingAssistantText?: boolean;
}

// ── Module-level constants ────────────────────────────────────────────────────

// Defined at module scope so it is not recreated on every render.
const streamingTitleSx = {
  "@keyframes reasoningShine": {
    "0%": { backgroundPosition: "120% center" },
    "100%": { backgroundPosition: "-120% center" },
  },
  display: "inline-block",
  backgroundImage: [
    "linear-gradient(95deg,",
    "var(--chakra-colors-gray-700) 0%,",
    "var(--chakra-colors-gray-700) 42%,",
    "var(--chakra-colors-purple-100) 50%,",
    "var(--chakra-colors-purple-200) 51%,",
    "var(--chakra-colors-purple-300) 52%,",
    "var(--chakra-colors-gray-700) 58%,",
    "var(--chakra-colors-gray-700) 100%)",
  ].join(" "),
  backgroundSize: "220% 100%",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  color: "transparent",
  animation: "reasoningShine 2.4s ease-in-out infinite",
} as const;

const ExecutionStepsCard: React.FC<ExecutionStepsCardProps> = ({
  onArtifactClick,
  executionSteps,
  streamingReasoningGroups,
  timelineOrder,
  prevMessageEndTimestampMs,
  isStreaming = false,
  hasStreamingAssistantText = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const wasStreamingRef = useRef(isStreaming);
  const wasRespondingPhaseRef = useRef(false);

  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming) {
      setIsExpanded(false);
    }
    wasStreamingRef.current = isStreaming;
  }, [isStreaming]);

  const stepCount = executionSteps?.length ?? 0;
  const toolRuns = useMemo(() => groupExecutionStepsByToolRun(executionSteps), [executionSteps]);
  const toolRunCount = toolRuns.length;

  const hasIncompleteToolRun = useMemo(() => toolRuns.some(r => !r.completed && !r.failed), [toolRuns]);

  /** Assistant tokens streaming after tools finished — show final “Worked for” card collapsed, no “Responding” row. */
  const isRespondingPhase = isStreaming && hasStreamingAssistantText && !hasIncompleteToolRun;
  const useCompleteStyleCard = !isStreaming || isRespondingPhase;
  const showStreamingStatusRow = isStreaming && !isRespondingPhase;

  useEffect(() => {
    if (isRespondingPhase && !wasRespondingPhaseRef.current) {
      setIsExpanded(false);
    }
    wasRespondingPhaseRef.current = isRespondingPhase;
  }, [isRespondingPhase]);

  // Compute the total "Worked for X" span.
  // On reload, execution_steps do not include the very first EXECUTING_LLM_CALL
  // (before the first REASONING_SUMMARY), so we anchor the start to the human
  // message timestamp (prevMessageEndTimestampMs) when available. This matches
  // the live-streaming measurement which does capture that initial LLM call.
  const workedDuration = useMemo(() => {
    const steps = executionSteps ?? [];
    const bounds = minMaxExecutionStepTimestampsMs(steps);
    if (!bounds) return null;
    const startMs =
      prevMessageEndTimestampMs !== undefined ? Math.min(prevMessageEndTimestampMs, bounds.minMs) : bounds.minMs;
    return formatDurationFromDeltaMs(bounds.maxMs - startMs);
  }, [executionSteps, prevMessageEndTimestampMs]);

  const completeTitlePlain = workedDuration ? `Worked for ${workedDuration}` : "Worked";
  const groupCountLabel = `${toolRunCount} ${toolRunCount === 1 ? "step" : "steps"}`;

  const streamStatusLabel = hasIncompleteToolRun ? "Working ... " : "Thinking ... ";

  const timelineItems = useMemo((): TimelineItem[] => {
    if (timelineOrder) {
      // ── Streaming case ────────────────────────────────────────────────────
      // timelineOrder was built by Chat.tsx in stream arrival order — no sorting needed.
      return timelineOrder.flatMap((entry): TimelineItem[] => {
        if (entry.kind === TimelineKind.REASONING) {
          const group = streamingReasoningGroups?.[entry.idx];
          return group ? [{ kind: TimelineKind.REASONING, group }] : [];
        }
        const run = toolRuns[entry.idx];
        return run ? [{ kind: TimelineKind.RUN, run, runIndex: entry.idx }] : [];
      });
    }

    // ── Non-streaming (historical) case ──────────────────────────────────────
    // Steps are already in generation order. Walk them once:
    //   REASONING_SUMMARY  → thought block (reasoning_summary is the full text)
    //   anything else       → feed into tool-run grouping (already done via toolRuns)
    // For duration: use the gap to the very next step in the array.
    const steps = executionSteps ?? [];
    const items: TimelineItem[] = [];
    let runIndex = 0;

    let silentIdx = -100;
    steps.forEach((step, i) => {
      if (step.status === ExecutionStatusType.REASONING_SUMMARY) {
        // Duration: first step anchors to human-message timestamp; later steps
        // use the preceding step's timestamp.
        const prevTs = i === 0 ? prevMessageEndTimestampMs : Number(steps[i - 1]?.timestamp_ms ?? 0);
        const rawDelta = step.timestamp_ms && prevTs ? Number(step.timestamp_ms) - prevTs : undefined;
        const durationMs = rawDelta !== undefined ? Math.max(0, rawDelta) : undefined;
        items.push({
          kind: TimelineKind.REASONING,
          group: {
            index: i,
            text: step.reasoning_summary ?? "",
            isOpen: false,
            startTimeMs: Number(step.timestamp_ms ?? 0),
            durationMs,
          },
        });
      } else if (step.status === ExecutionStatusType.EXECUTING_LLM_CALL) {
        // Detect silent LLM calls: an EXECUTING_LLM_CALL that is NOT followed
        // by a REASONING_SUMMARY before the next tool call or LLM call fires.
        // Such calls produced no reasoning tokens, so no REASONING_SUMMARY was
        // persisted. We add a silent (empty-text) thought group for them.
        let nextSignificant: ExecutionStepProto | undefined;
        for (let j = i + 1; j < steps.length; j++) {
          const s = steps[j];
          if (
            s.status === ExecutionStatusType.REASONING_SUMMARY ||
            s.status === ExecutionStatusType.EXECUTING_TOOL_CALL ||
            s.status === ExecutionStatusType.EXECUTING_LLM_CALL
          ) {
            nextSignificant = s;
            break;
          }
        }
        // Only add when the next significant step is NOT a reasoning summary
        // (i.e. this call was truly silent) and we have a next step to bound
        // the duration.
        if (nextSignificant && nextSignificant.status !== ExecutionStatusType.REASONING_SUMMARY && step.timestamp_ms) {
          const startMs = Number(step.timestamp_ms);
          const endMs = nextSignificant.timestamp_ms ? Number(nextSignificant.timestamp_ms) : undefined;
          const durationMs = endMs !== undefined ? endMs - startMs : undefined;
          items.push({
            kind: TimelineKind.REASONING,
            group: {
              index: silentIdx--,
              text: "",
              isOpen: false,
              startTimeMs: startMs,
              durationMs,
            },
          });
        }
      } else if (step.status === ExecutionStatusType.EXECUTING_TOOL_CALL) {
        // Each EXECUTING_TOOL_CALL opens a new tool run (same grouping as toolRuns useMemo).
        const run = toolRuns[runIndex];
        if (run) {
          items.push({ kind: TimelineKind.RUN, run, runIndex });
          runIndex++;
        }
      } else if (step.status === ExecutionStatusType.CREATING_FILE_ARTIFACT) {
        // File upload is a standalone completed run — no EXECUTING_TOOL_CALL wrapper.
        const run = toolRuns[runIndex];
        if (run) {
          items.push({ kind: TimelineKind.RUN, run, runIndex });
          runIndex++;
        }
      }
      // All other statuses (TOOL_CALL_SUCCESS etc.) belong to the current run
      // and are already captured inside toolRuns[].steps — skip here.
    });

    return items;
  }, [timelineOrder, streamingReasoningGroups, toolRuns, executionSteps, prevMessageEndTimestampMs]);

  const hasReasoningGroups = (streamingReasoningGroups?.length ?? 0) > 0;
  const hasNonStreamingReasoning =
    !timelineOrder && (executionSteps ?? []).some(s => s.status === ExecutionStatusType.REASONING_SUMMARY);
  const hasTimelineContent = toolRunCount > 0 || hasReasoningGroups || hasNonStreamingReasoning;

  if (!isStreaming && stepCount === 0 && !hasTimelineContent) {
    return null;
  }

  // Timeline renderer: reasoning groups (ThoughtBlock) and tool runs (ToolRunRow) interleaved
  // in chronological order. Connector-line state is computed from runIndex within toolRuns.
  const timelineItemsRenderer = (
    <VStack spacing={0} align="flex-start" px={3} py={2} w="100%">
      <VStack align="stretch" spacing={0.5} w="100%">
        {timelineItems.map(item => {
          if (item.kind === TimelineKind.REASONING) {
            return <ThoughtBlock key={item.group.index} group={item.group} isActivelyStreaming={isStreaming} />;
          }
          const { run, runIndex } = item;
          return (
            <ToolRunRow
              key={`run-${runIndex}-${run.steps[0]?.timestamp_ms ?? runIndex}`}
              run={run}
              isFirstRun={runIndex === 0}
              isLastRun={runIndex === toolRuns.length - 1}
              prevRunDone={runIndex > 0 && (toolRuns[runIndex - 1]!.completed || toolRuns[runIndex - 1]!.failed)}
              onArtifactClick={onArtifactClick}
            />
          );
        })}
      </VStack>
    </VStack>
  );

  return (
    <VStack w="100%" maxW="100%" minW={0} alignSelf="stretch" align="stretch" spacing={2} m={0}>
      {hasTimelineContent ? (
        <Box
          bg="white"
          borderRadius="md"
          w="100%"
          maxW="100%"
          minW={0}
          overflow="hidden"
          border="1px solid"
          borderColor="gray.300"
        >
          {useCompleteStyleCard ? (
            <>
              <Flex
                w="100%"
                align="center"
                justify="flex-start"
                gap={2}
                p={2}
                px={3}
                cursor="pointer"
                onClick={() => setIsExpanded(!isExpanded)}
                _hover={{ bg: "gray.100" }}
                transition="background 0.2s"
              >
                <Box minW={0} textAlign="left">
                  {/* When Expanded it will show the "Worked for X seconds" */}
                  {/* When Collapsed it will show the "Worked for X seconds (X steps)" */}
                  {isExpanded ? (
                    <Text fontSize="sm" color="gray.700" fontWeight="600" mb={0} textAlign="left">
                      {completeTitlePlain}
                    </Text>
                  ) : (
                    <HStack as="span" display="inline-flex" spacing={1} align="baseline" flexWrap="wrap" mb={0}>
                      <Text as="span" fontSize="sm" color="gray.700" fontWeight="600">
                        {completeTitlePlain}
                      </Text>
                      {toolRunCount > 1 && (
                        <Text as="span" fontSize="sm" color="gray.500" fontWeight="500">
                          ({groupCountLabel})
                        </Text>
                      )}
                    </HStack>
                  )}
                </Box>
                <Icon
                  as={isExpanded ? AppIcons.chevronDown : AppIcons.chevronRight}
                  boxSize={3}
                  color="gray.600"
                  flexShrink={0}
                />
              </Flex>
              <Collapse in={isExpanded} animateOpacity>
                {timelineItemsRenderer}
              </Collapse>
            </>
          ) : (
            // Streaming (not yet in responding phase): show unified timeline inline
            timelineItemsRenderer
          )}
        </Box>
      ) : null}
      {showStreamingStatusRow ? (
        <HStack w="100%" spacing={2} align="center" justify="flex-start">
          <AgentThinkingLogo boxSize="21px" />
          <Text as="span" fontSize="md" fontWeight="500" lineHeight="short" mb={0} sx={streamingTitleSx}>
            {streamStatusLabel}
          </Text>
        </HStack>
      ) : null}
    </VStack>
  );
};

export default ExecutionStepsCard;
